import React from "react";
import { Space, Input, Checkbox } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { PlusCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { OptionItems } from "./SurveySlice";
import i18n from "i18n";
import {
  Sortable,
  Props as SortableProps,
} from "./sortable_choice/SortableChoiceList";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { v4 as uuidv4 } from "uuid";

const { TextArea } = Input;

interface QuestionEditCheckboxFormProps {
  handleCheckboxItemsChange: (checkboxItems: Array<OptionItems>) => void;
  checkboxItems: Array<OptionItems>;

  callParentHasCustomText: () => void;
  hasCustomText: boolean;

  callParentTextPlaceholderChange: (placeholder: string) => void;
  customTextPlaceholder: string;

  callNotPromptCheckboxChange: (index: number, checked: boolean) => void;

  isExcludeChoiceLabel: boolean;
  handleExcludeChoiceLabelChange: (e: CheckboxChangeEvent) => void;
}

const QuestionEditCheckboxForm: React.FC<QuestionEditCheckboxFormProps> = ({
  handleCheckboxItemsChange,
  checkboxItems,
  callParentHasCustomText,
  hasCustomText,
  callParentTextPlaceholderChange,
  customTextPlaceholder,
  callNotPromptCheckboxChange,
}) => {
  const appendOption = (index: number): Array<OptionItems> => {
    let newItems = [...checkboxItems];

    const newItem: OptionItems = {
      name: "",
      value: "",
      dndId: uuidv4(),
    };

    newItems.splice(index + 1, 0, newItem);
    newItems = newItems.map((item, index) => {
      return { ...item, name: `checkbox${index + 1}` };
    });

    return [...newItems];
  };

  const deleteOption = (index: number) => {
    let newItems = [...checkboxItems];
    newItems.splice(index, 1);
    newItems = newItems.map((item, index) => {
      return { ...item, name: `checkbox${index + 1}` };
    });

    return newItems;
  };

  const handleClickPlusButton = (index: number) => {
    handleCheckboxItemsChange(appendOption(index));
  };

  const handleClickMinusButton = (index: number) => {
    handleCheckboxItemsChange(deleteOption(index));
  };

  const customTextCheckboxChange = () => {
    callParentHasCustomText();
  };

  const notPromptCheckboxChange = (index: number) => (
    e: CheckboxChangeEvent
  ) => {
    if (callNotPromptCheckboxChange) {
      callNotPromptCheckboxChange(index, e.target.checked);
    }
  };

  const customTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    callParentTextPlaceholderChange(e.target.value);
  };

  const handleReorder = (reorderedItems: Array<OptionItems>) => {
    // nameを再採番
    const newItems = reorderedItems.map((item, index) => {
      return { ...item, name: `checkbox${index + 1}` };
    });

    handleCheckboxItemsChange(newItems);
  };

  const sortableProps: Partial<SortableProps> = {
    strategy: verticalListSortingStrategy,
    selectItems: checkboxItems,
    handle: true,
    onReorder: handleReorder,
    renderContent: (item, index) => {
      const isDeleteVisible = index === 0 ? "hidden" : undefined;
      return (
        <div className="question-item-horizontal">
          <Checkbox
            name="checkbox"
            disabled
            className="question-item-horizontal"
          >
            <Input
              name={item.name}
              value={item.value}
              onChange={(e) => {
                const value = e.target.value;
                const newItem = checkboxItems.map((checkboxItem, i) =>
                  i === index ? { ...checkboxItem, value } : checkboxItem
                );
                handleCheckboxItemsChange(newItem);
              }}
            />
          </Checkbox>
          <PlusCircleOutlined
            onClick={() => handleClickPlusButton(index)}
            className="question-item-option-icon"
            style={{ color: "blue" }}
          />
          <MinusCircleOutlined
            onClick={() => handleClickMinusButton(index)}
            className="question-item-option-icon"
            style={{
              color: "red",
              visibility: isDeleteVisible,
            }}
          />
          <Checkbox
            checked={item.notPrompt}
            onChange={notPromptCheckboxChange(index)}
            style={{ marginLeft: "5px" }}
          >
            {i18n.t("survey.notPrompt")}
          </Checkbox>
        </div>
      );
    },
  };

  return (
    <>
      <Space
        direction="vertical"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        <Sortable {...sortableProps} />
      </Space>
      <div className="radio-free-text">
        <Checkbox
          checked={hasCustomText}
          onChange={customTextCheckboxChange}
          style={{ marginBottom: "5px" }}
        >
          {i18n.t("survey.freeTextLabel")}
        </Checkbox>
        <TextArea
          rows={4}
          name="placeholder"
          placeholder={i18n.t("survey.editPlaceholder")}
          disabled={!hasCustomText}
          onChange={customTextChange}
          value={customTextPlaceholder}
        />
      </div>
    </>
  );
};

export default QuestionEditCheckboxForm;
